import type { MentionData } from '../context/MentionContext'

/**
 * How much *earlier* than the sent row a server copy may be stamped and still
 * be the same reply. Both timestamps describe the same moment — the sent row
 * carries the client clock at send time, the server copy the server's record of
 * that same send — so this only absorbs clock skew, never the wait for the copy
 * to arrive. Kept tight so an identical reply sent minutes ago, already
 * persisted, can't retire a fresh send and hide it.
 */
const SENT_REPLY_CLOCK_SKEW_MS = 60 * 1000

/**
 * How much *later* than the sent row a server copy may be stamped. Wider than
 * the skew allowance because a reply is stamped when the service publishes it,
 * which can trail the send.
 */
const SENT_REPLY_PUBLISH_DELAY_MS = 5 * 60 * 1000

/**
 * How far apart a sent reply and a server reply that could be its persisted
 * copy are stamped, or null when the server reply cannot be that copy.
 *
 * Ids can't answer this: a sent reply carries a synthetic id because the
 * mention reply mutation returns the parent, not the reply, so the server copy
 * arrives under an id the sent row never had.
 *
 * Text can't answer it either, so it is deliberately not compared. A service
 * rewrites the reply body as it publishes: X prepends the thread participants'
 * handles, turning "Nice one" into "@them @us Nice one", so the stored text
 * routinely differs from what was typed.
 *
 * What remains is own authorship plus a timestamp consistent with the send. The
 * server stamps the reply when it publishes it, seconds after the client
 * stamped the sent row for the same send. A reply with no usable timestamp
 * cannot be paired, and showing it twice beats hiding a reply that was really
 * sent.
 */
const persistedCopyDriftMs = (
  sent: MentionData,
  server: MentionData,
): number | null => {
  if (!server.isOwnEngagement) return null
  if (sent.publishedAt == null || server.publishedAt == null) return null
  const sentAt = new Date(sent.publishedAt).getTime()
  const serverAt = new Date(server.publishedAt).getTime()
  if (Number.isNaN(sentAt) || Number.isNaN(serverAt)) return null
  if (serverAt < sentAt - SENT_REPLY_CLOCK_SKEW_MS) return null
  if (serverAt > sentAt + SENT_REPLY_PUBLISH_DELAY_MS) return null
  return Math.abs(serverAt - sentAt)
}

/**
 * Index of the sent reply this server reply is the persisted copy of, or -1.
 *
 * An id match is definitive and wins outright. Otherwise the closest timestamp
 * wins: the window spans minutes, so every send in a burst qualifies for every
 * copy, and taking the first qualifying one would pair a copy with a send it
 * does not belong to and hide the send that copy really came from. Equal drift
 * keeps the earlier send, which is the one more likely already persisted.
 */
const claimableSentReplyIndex = (
  sentReplies: readonly MentionData[],
  server: MentionData,
): number => {
  let bestIndex = -1
  let bestDrift = Number.POSITIVE_INFINITY

  for (const [index, sent] of sentReplies.entries()) {
    if (sent.id === server.id) return index

    const drift = persistedCopyDriftMs(sent, server)
    if (drift !== null && drift < bestDrift) {
      bestDrift = drift
      bestIndex = index
    }
  }

  return bestIndex
}

/**
 * Merges locally-sent replies with the server replies for the same mention.
 * Sent replies arrive newest-first and keep that order, so they lead.
 *
 * A sent reply drops out once the server holds its copy. Each server reply
 * claims at most one sent reply, so two quick sends keep the one the server has
 * not confirmed yet rather than collapsing onto a single copy.
 */
export const mergeSentReplies = (
  sentReplies: readonly MentionData[],
  serverReplies: readonly MentionData[],
): MentionData[] => {
  // Paired oldest-first so drift comparisons read chronologically. The
  // newest-first order the sent replies arrive in is restored on the way out.
  const unclaimed = sentReplies.toReversed()

  for (const server of serverReplies) {
    const claimIndex = claimableSentReplyIndex(unclaimed, server)
    if (claimIndex !== -1) unclaimed.splice(claimIndex, 1)
  }

  return [...unclaimed.toReversed(), ...serverReplies]
}
